import type { DataTableColumn } from '../../../components/ui/data-table';
import { Badge } from '../../../components/ui/badge';
import ActionDropdown from '../../../components/common/action-dropdown';
import { getStatusColor, capitalizeFirst } from '../../../utils';
import { MT5AccountUser } from '../../../utils/types';

export const createDemoAccountColumns = (options: {
  onViewDetails: (login: number) => void;
  onRefreshAccount: (login: number) => void;
  onChangeLeverage?: (account: MT5AccountUser) => void;
}): DataTableColumn<MT5AccountUser>[] => [
  {
    id: 'Login',
    header: 'Login',
    accessor: row => {
      const val = row.Login ?? (row as any).mt5Login ?? '-';
      return <span className="font-mono text-sm font-semibold">{val}</span>;
    },
    sortable: true,
    sortValue: row => row.Login ?? (row as any).mt5Login ?? 0,
  },
  {
    id: 'Name',
    header: 'Name',
    accessor: row => {
      const val = row.Name || (row as any).name || '-';
      return <span className="text-sm font-medium">{val}</span>;
    },
    sortable: true,
    sortValue: row => row.Name || (row as any).name || '',
  },
  {
    id: 'Group',
    header: 'Group',
    accessor: row => {
      const val = row.Group || (row as any).group || '-';
      return (
        <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
          {val}
        </Badge>
      );
    },
    sortable: true,
    sortValue: row => row.Group || (row as any).group || '',
  },
  {
    id: 'currency',
    header: 'Currency',
    accessor: row => (row as any).currency || '-',
    sortable: true,
    sortValue: row => (row as any).currency || '',
  },
  {
    id: 'Balance',
    header: 'Balance',
    accessor: row => {
      const val = Number(row.Balance ?? (row as any).balance ?? 0);
      return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
    },
    sortable: true,
    sortValue: row => Number(row.Balance ?? (row as any).balance ?? 0),
  },
  {
    id: 'Credit',
    header: 'Credit',
    accessor: row => {
      const val = Number(row.Credit ?? (row as any).credit ?? 0);
      return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
    },
    sortable: true,
    sortValue: row => Number(row.Credit ?? (row as any).credit ?? 0),
  },
  {
    id: 'Leverage',
    header: 'Leverage',
    accessor: row => {
      const val = row.Leverage ?? (row as any).leverage;
      return val !== undefined && val !== null && String(val).trim() !== ''
        ? String(val)
        : '-';
    },
    sortable: true,
    sortValue: row => Number(row.Leverage ?? (row as any).leverage ?? 0),
  },
  {
    id: 'Email',
    header: 'Email',
    accessor: row => {
      const val = row.Email || (row as any).email || '-';
      return <span className="text-sm text-gray-500">{val}</span>;
    },
    sortable: true,
    sortValue: row => row.Email || (row as any).email || '',
  },
  {
    id: 'Country',
    header: 'Country',
    accessor: row => {
      const val = row.Country || (row as any).country || '-';
      return <span className="text-sm">{val}</span>;
    },
    sortable: true,
    sortValue: row => row.Country || (row as any).country || '',
  },
  {
    id: 'Status',
    header: 'Status',
    accessor: row => {
      const val = row.Status || (row as any).status || 'Active';
      return (
        <Badge className={getStatusColor(val)}>{capitalizeFirst(val)}</Badge>
      );
    },
    sortable: true,
    sortValue: row => row.Status || (row as any).status || '',
  },
  {
    id: 'actions',
    header: 'Actions',
    accessor: row => {
      const loginVal = Number(row.Login ?? (row as any).mt5Login ?? 0);
      return (
        <ActionDropdown
          id={String(loginVal)}
          onView={() => options.onViewDetails(loginVal)}
          showEdit={Boolean(options.onChangeLeverage)}
          showDelete={false}
          showRefresh={false}
          onRefresh={() => options.onRefreshAccount(loginVal)}
          onEdit={
            options.onChangeLeverage
              ? () => options.onChangeLeverage?.(row)
              : undefined
          }
        />
      );
    },
  },
];
